import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/Button';
import { Spinner } from '@/components/Spinner';
import { PrinterDetailView, PrinterForm } from '@/features/printers/components';
import { useTenantPrinter, useUpdateTenantPrinter } from '@/features/printers/hooks';
import type { UpdateTenantPrinterInput } from '@maker-toolchest/shared';

export function PrinterDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);

  const { data: printer, isLoading, error } = useTenantPrinter(id!);
  const updatePrinter = useUpdateTenantPrinter();

  const handleUpdatePrinter = async (data: UpdateTenantPrinterInput) => {
    if (!id) return;
    await updatePrinter.mutateAsync({ id, input: data });
    setIsEditFormOpen(false);
  };

  const handleBack = () => {
    navigate('/printers');
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !printer) {
    return (
      <div className="p-4">
        <div className="mb-4">
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ArrowLeft className="h-4 w-4" />}
            onClick={handleBack}
          >
            Back to Printers
          </Button>
        </div>
        <div className="rounded-lg border border-border bg-background-subtle p-8 text-center">
          <p className="text-error">
            {error ? 'Failed to load printer details' : 'Printer not found'}
          </p>
          <Button variant="secondary" onClick={handleBack} className="mt-4">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header with back button */}
      <div className="border-b border-border bg-background-subtle px-4 py-3">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<ArrowLeft className="h-4 w-4" />}
          onClick={handleBack}
        >
          Back to Printers
        </Button>
      </div>

      {/* Detail view */}
      <div className="flex-1 overflow-hidden">
        <PrinterDetailView
          printer={printer}
          onEdit={() => setIsEditFormOpen(true)}
        />
      </div>

      {/* Edit Form */}
      <PrinterForm
        open={isEditFormOpen}
        onClose={() => setIsEditFormOpen(false)}
        onSubmit={handleUpdatePrinter as any}
        printer={printer}
        loading={updatePrinter.isPending}
      />
    </div>
  );
}
